// Membership benefits: reading them, labelling them, summarising them.
//
// Pure by design: no Firebase, no React. Membership definitions are authored in this app
// but every benefit is APPLIED elsewhere - the activity discount and the entry quota are
// resolved at purchase in the backend, and the continuous charge is executed there too. So
// everything here reads and formats; nothing computes entitlement.
//
// A note on stale fields. Definitions are written with set({merge:true}) and rjsf only
// renders the branch matching the current discriminator, so a membership that was once sold
// in the shop and later taken out of it still carries `continuous`, `validity` and friends.
// Dispatch strictly on the discriminator and ignore fields that do not belong to the
// selected branch - the same rule docs/functions-to-update.txt states for benefit cards.

import { endsBefore } from './purchases'

export const BENEFIT_TYPES = [
  { value: 'discount', label: 'Jäsenalennus' },
  { value: 'activityentries', label: 'Aktiviteettien vapaa käyttö' },
  { value: 'activitydiscount', label: 'Aktiviteettialennus' },
]

export const benefitTypeLabel = (value) => {
  const found = BENEFIT_TYPES.find((b) => b.value === value)
  return found ? found.label : value || ''
}

// The window an entry quota is measured over. Genitive-ish short forms, so the quota reads
// as '3 kertaa/viikko'.
export const ENTRY_WINDOWS = [
  { value: 'day', label: 'päivä' },
  { value: 'week', label: 'viikko' },
  { value: 'month', label: 'kuukausi' },
  { value: 'year', label: 'vuosi' },
]

export const entryWindowLabel = (value) => {
  const found = ENTRY_WINDOWS.find((w) => w.value === value)
  return found ? found.label : value || ''
}

const num = (value) => {
  const parsed = typeof value === 'number' ? value : parseFloat(String(value === null || value === undefined ? '' : value).replace(',', '.'))
  return isFinite(parsed) ? parsed : null
}

/* Reading the benefits */

export const benefitsOf = (membership) =>
  membership && Array.isArray(membership.benefits) ? membership.benefits.filter(Boolean) : []

const benefitOfType = (membership, benefittype) =>
  benefitsOf(membership).find((b) => b.benefittype === benefittype) || null

// The activity entry quota: N times per window. Already in the schema as 'activityentries'
// long before this work - it was simply never read by anything.
export const activityEntries = (membership) => {
  const benefit = benefitOfType(membership, 'activityentries')
  if (!benefit) return null
  const count = num(benefit.entrycount)
  if (count === null || count <= 0) return null
  return {
    count,
    window: benefit.entrywindow || '',
    // Both hold KEYS, and an empty list means no restriction on that dimension.
    sports: Array.isArray(benefit.sports) ? benefit.sports.filter(Boolean) : [],
    activitytypes: Array.isArray(benefit.activitytypes) ? benefit.activitytypes.filter(Boolean) : [],
  }
}

// The percent discount on activities, optionally restricted to certain sports.
//
// sports[] holds sport KEYS, not labels. Keys are slugified and frozen once saved, labels
// are freely renameable - and activity.sport happens to store the label, so whoever
// resolves this has to map through config/sports. An empty list means every sport, the
// convention already used for message-template sports.
export const activityDiscount = (membership) => {
  const benefit = benefitOfType(membership, 'activitydiscount')
  if (!benefit) return null
  const percent = num(benefit.discountpercent)
  if (percent === null || percent <= 0) return null
  return { percent, sports: Array.isArray(benefit.sports) ? benefit.sports.filter(Boolean) : [] }
}

// Usage caps on the member pricelist ('discount'): the pricelist applies at most N times
// per window, after which the benefit is not valid and the reservation is priced normally.
// 0 (the schema default) or empty means no cap in that window. Ordered day -> year.
const PRICING_CAP_FIELDS = [
  { field: 'maxperday', window: 'day' },
  { field: 'maxperweek', window: 'week' },
  { field: 'maxpermonth', window: 'month' },
  { field: 'maxperyear', window: 'year' },
]

export const memberPricingCaps = (membership) => {
  const benefit = benefitOfType(membership, 'discount')
  if (!benefit) return []
  return PRICING_CAP_FIELDS.map(({ field, window }) => ({ window, count: num(benefit[field]) })).filter(
    (cap) => cap.count !== null && cap.count > 0
  )
}

// Continuous charging requires BOTH flags: the field only exists inside the
// availableinshop === 'true' branch, so a membership taken out of the shop can be left
// carrying a stale continuous: true. Note availableinshop is the STRING 'true'.
export const isContinuous = (membership) =>
  !!membership && membership.availableinshop === 'true' && membership.continuous === true

/* Sports */

// Merges the venue's own config/sports list over DEFAULT_SPORTS. Customer entries come
// second so a same-key override wins, and the result is unique by key and sorted by label.
// The idiom is currently repeated verbatim in four containers.
export const mergeSports = (defaults, configList) => {
  const all = [...(defaults || [])]
  if (Array.isArray(configList)) all.push(...configList.filter(Boolean))
  const unique = [...new Map(all.filter((s) => s && s.key).map((s) => [s.key, s])).values()]
  unique.sort((a, b) => String(a.label || '').localeCompare(String(b.label || ''), 'fi'))
  return unique
}

// Labels for a list of keys, SKIPPING keys the list does not know. Works for sports and
// activity types alike - both are { key, label } lists merged the same way.
//
// MessagingListsContainer does `.find(...).label` unguarded and throws on a venue's own
// custom sport key. This is the safe version: an unknown key is dropped rather than
// crashing the view.
export const labelsForKeys = (keys, list) => {
  const options = list || []
  return (keys || [])
    .map((key) => options.find((o) => o && o.key === key))
    .filter(Boolean)
    .map((o) => o.label)
}

// Reads better at the call sites that really are about sports.
export const sportLabels = labelsForKeys

/* Formatting */

// Segments are prefixed because the quota has TWO restriction dimensions - without
// 'lajit:'/'tyypit:' a bare '(Valmennus)' could be read as either.
const limitText = (entries, sports, activitytypes) => {
  const parts = []
  if (entries.sports.length > 0) {
    const labels = labelsForKeys(entries.sports, sports)
    parts.push('lajit: ' + (labels.length > 0 ? labels.join(', ') : entries.sports.length + ' kpl'))
  }
  if (entries.activitytypes.length > 0) {
    const labels = labelsForKeys(entries.activitytypes, activitytypes)
    parts.push('tyypit: ' + (labels.length > 0 ? labels.join(', ') : entries.activitytypes.length + ' kpl'))
  }
  return parts.join(' · ')
}

export const formatEntryQuota = (entries, sports, activitytypes) => {
  if (!entries || !entries.count) return ''
  const window = entryWindowLabel(entries.window)
  const quota = entries.count + ' kertaa' + (window ? '/' + window : '')
  const limits = limitText(entries, sports, activitytypes)
  return limits ? quota + ' (' + limits + ')' : quota
}

export const formatActivityDiscount = (discount, sports) => {
  if (!discount || !discount.percent) return ''
  const percent = '-' + discount.percent + ' %'
  if (discount.sports.length === 0) return percent + ' (kaikki lajit)'
  const labels = sportLabels(discount.sports, sports)
  // Every key was unknown - say how many rather than pretending there is no restriction,
  // which '(kaikki lajit)' would wrongly imply.
  if (labels.length === 0) return percent + ' (' + discount.sports.length + ' lajia)'
  return percent + ' (' + labels.join(', ') + ')'
}

// Same wording as the entry quota, so '2 kertaa/päivä' reads alike in both.
export const formatMemberPricingCaps = (caps) =>
  (caps || []).map((cap) => cap.count + ' kertaa/' + entryWindowLabel(cap.window)).join(', ')

// One line for the memberships grid, so a quota is visible without opening the form.
export const benefitsSummary = (membership, sports, activitytypes) => {
  const parts = []
  const entries = activityEntries(membership)
  if (entries) parts.push(formatEntryQuota(entries, sports, activitytypes))
  const discount = activityDiscount(membership)
  if (discount) parts.push(formatActivityDiscount(discount, sports))
  // The pricelist-reference discount carries no number worth showing here, only that it
  // is set - the pricelist name would need a second lookup the grid does not have. Its
  // usage caps, though, are worth showing.
  if (benefitOfType(membership, 'discount')) {
    const caps = formatMemberPricingCaps(memberPricingCaps(membership))
    parts.push(caps ? 'Jäsenhinnasto (' + caps + ')' : 'Jäsenhinnasto')
  }
  if (isContinuous(membership)) parts.push('Jatkuva laskutus')
  return parts.join(' · ')
}

/* Continuous charge status on an assignment */

// Read-only, and every field is optional: nothing writes these yet. Returns null when the
// backend has told us nothing, so the UI can show a dash instead of inventing a state.
//
// Field names are the ones proposed to the backend in docs/functions-to-update.txt; until
// they land this returns null for every assignment.
export const CHARGE_STATES = [
  { value: 'active', label: 'Voimassa' },
  { value: 'paused', label: 'Tauolla' },
  { value: 'failed', label: 'Veloitus epäonnistui' },
  { value: 'cancelled', label: 'Peruttu' },
]

export const chargeStateLabel = (value) => {
  const found = CHARGE_STATES.find((s) => s.value === value)
  return found ? found.label : value || ''
}

export const chargeStatus = (assignment) => {
  if (!assignment) return null
  const known = ['cardstored', 'nextcharge', 'lastcharge', 'chargefailures', 'chargestate']
  const present = known.some((key) => assignment[key] !== undefined && assignment[key] !== null)
  if (!present) return null
  return {
    cardstored: assignment.cardstored === true,
    nextcharge: assignment.nextcharge || '',
    lastcharge: assignment.lastcharge || '',
    failures: num(assignment.chargefailures) || 0,
    state: assignment.chargestate || '',
  }
}

// The one-line rendering of the above.
export const formatChargeStatus = (status) => {
  if (!status) return ''
  const parts = []
  if (status.state) parts.push(chargeStateLabel(status.state))
  parts.push(status.cardstored ? 'Kortti talletettu' : 'Ei korttia')
  if (status.nextcharge) parts.push('seuraava ' + status.nextcharge)
  if (status.failures > 0) parts.push(status.failures + ' epäonnistunut veloitus')
  return parts.join(' · ')
}

/* The date range a membership was sold for */

// Payment reading lives in utils/purchases.js: it is true of any purchase, and the benefit
// card listing renders the same thing from the same collection.

// The end date the memberships listing actually displays. A permanent membership has no
// end - it carries a far-future sentinel (2050-12-31) that is not worth showing - and
// `validuntil` wins over `end` because that is the precedence this view has always used,
// noted as a divergence from the person and organisation views in
// docs/features/memberships.md.
export const effectiveEnd = (row) => {
  if (!row || row.permanent) return null
  return row.validuntil || row.end || null
}

// Permanent rows are exempt: they have no meaningful end to be before anything.
export const endsBeforeStart = (row) => endsBefore(row && row.start, effectiveEnd(row))
